import { useEffect, useState } from 'react';
import HeroSection from './components/HeroSection';
import CategorySection from './components/CategorySection';
import FeatureCards from './components/FeatureCards';
import ComparisonTable from './components/ComparisonTable';
import WorkflowSteps from './components/WorkflowSteps';
import ArticleCards from './components/ArticleCards';
import FaqSection from './components/FaqSection';
import Footer from './components/Footer';
import { Link } from 'react-router-dom';
import { faqItems, categories } from '@/mocks/homeData';

export default function Home() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const faqSchema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqItems.map((item) => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: item.answer,
        },
      })),
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(faqSchema);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  return (
    <div className="min-h-screen bg-background-50">
      <nav className="sticky top-0 z-40 bg-white border-b border-background-200">
        <div className="max-w-6xl mx-auto px-4 md:px-6 flex items-center justify-between h-14 md:h-16">
          <Link to="/" className="flex items-center gap-2 flex-shrink-0">
            <span className="text-lg md:text-xl font-heading font-bold text-foreground-900">
              Basetao Sheet
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-6">
            <Link
              to="/"
              className="whitespace-nowrap text-sm font-medium text-foreground-800 hover:text-primary-600 transition-colors duration-150 cursor-pointer"
            >
              Home
            </Link>
            <Link
              to="/article/ultimate-basetao-spreadsheet-guide"
              className="whitespace-nowrap text-sm font-medium text-foreground-800 hover:text-primary-600 transition-colors duration-150 cursor-pointer"
            >
              Shopping Guide
            </Link>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="whitespace-nowrap cursor-pointer inline-flex items-center gap-1.5 px-5 py-2.5 bg-primary-500 text-white text-sm font-semibold rounded-full hover:bg-primary-600 transition-all duration-200"
            >
              <i className="ri-external-link-line"></i>
              Shop Now
            </a>
          </div>

          <div className="flex md:hidden items-center gap-2">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="whitespace-nowrap cursor-pointer inline-flex items-center gap-1 px-3 py-1.5 bg-primary-500 text-white text-xs font-medium rounded-full hover:bg-primary-600 transition-all duration-200"
            >
              Shop
            </a>
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="w-9 h-9 flex items-center justify-center text-foreground-800 cursor-pointer"
              aria-label="Toggle menu"
            >
              <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl`}></i>
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden fixed inset-0 top-14 z-30 bg-white overflow-y-auto">
            <div className="px-4 py-4 space-y-1">
              <Link
                to="/"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-3 px-4 py-3 text-sm font-medium text-foreground-800 hover:bg-background-100 rounded-lg cursor-pointer"
              >
                <i className="ri-home-4-line text-lg text-primary-500"></i>
                Home
              </Link>
              <Link
                to="/article/ultimate-basetao-spreadsheet-guide"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-3 px-4 py-3 text-sm font-medium text-foreground-800 hover:bg-background-100 rounded-lg cursor-pointer"
              >
                <i className="ri-book-open-line text-lg text-primary-500"></i>
                Shopping Guide
              </Link>

              <div className="pt-3 pb-1 px-4">
                <span className="text-[11px] font-semibold text-foreground-500 uppercase tracking-wider">
                  Product Categories
                </span>
              </div>

              {categories.map((cat) => (
                <a
                  key={cat.id}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 rounded-lg cursor-pointer"
                >
                  <div className="w-7 h-7 rounded-md overflow-hidden flex-shrink-0 bg-background-100">
                    <img src={cat.image} alt={cat.name} className="w-full h-full object-cover" loading="lazy" />
                  </div>
                  <span>{cat.name}</span>
                </a>
              ))}

              <div className="pt-4 px-4">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setMobileMenuOpen(false)}
                  className="whitespace-nowrap cursor-pointer flex items-center justify-center gap-2 w-full px-5 py-3 bg-primary-500 text-white text-sm font-semibold rounded-full hover:bg-primary-600 transition-all duration-200"
                >
                  <i className="ri-external-link-line"></i>
                  Shop Now on Basetao
                </a>
              </div>
            </div>
          </div>
        )}
      </nav>

      <main>
        <HeroSection />
        <CategorySection />

        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 mb-3 text-center">
              What Is Basetao Spreadsheet?
            </h2>
            <p className="text-foreground-600 text-sm md:text-base leading-relaxed text-center max-w-3xl mx-auto">
              <strong>Basetao Spreadsheet</strong> is a structured resource system designed to help shoppers organize products, compare prices, track orders and improve their buying workflow. Instead of searching through hundreds of links, users can access product information, reviews, shipping strategies and shopping guides in one place.
            </p>
          </div>
        </section>

        <FeatureCards />
        <ComparisonTable />
        <WorkflowSteps />
        <ArticleCards />

        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 mb-4 text-center">
              Is Basetao Legit?
            </h2>
            <div className="bg-white border border-background-200 rounded-lg p-6 md:p-8">
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                Before using any shopping agent, users want to know whether the platform is reliable. Our Basetao Legit guide analyzes platform history, user experience, payment methods, shipping transparency, and common complaints to help you make an informed decision.
              </p>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                Basetao has been operating as a shopping agent for several years, serving international customers who want to purchase products from Chinese marketplaces. User experiences vary — many shoppers report positive experiences with competitive pricing and reliable service, while some note that shipping costs and customer support response times can differ from expectations.
              </p>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                The best approach is to start with a small test order, familiarize yourself with the platform's workflow, and use resources like our <Link to="/article/ultimate-basetao-spreadsheet-guide" className="text-primary-600 underline underline-offset-2 hover:text-primary-700">Basetao Spreadsheet Guide</Link> to stay organized throughout the process. For more detailed information, visit the official <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-primary-600 underline underline-offset-2 hover:text-primary-700">Basetao Spreadsheet</a> platform.
              </p>
            </div>
          </div>
        </section>

        <FaqSection />
      </main>

      <Footer />
    </div>
  );
}